import { useLiveQuery } from "@tanstack/react-db";
import { useQuery } from "@tanstack/react-query";
import { breedsLiveQueryOptions } from "@/features/breeds";
import {
    type CollectionWagsListing,
    useWagsListingsLiveQuery,
} from "@/features/listings";
import {
    findFeedQueryInput,
    getFindFeedQueryOptions,
} from "../feed/find-feed-query";
import { type FindFeedBundledResponse } from "../feed/find-feed";
import { POPULAR_BREED_NAMES } from "@/features/discovery";
import { useHydratedValue } from "@/lib/core/use-hydrated-value";
import { resolveFindLoadUi } from "./find-load-failure";
import { feedListingIds, nonEmptyFeedRows, projectFeedRows } from "./feedRows";

const popularBreedNames = POPULAR_BREED_NAMES;

function getPopularBreeds(
    breeds: Array<{
        id: string;
        name: string;
        size: string | null;
        imageUrl: string | null;
    }>,
) {
    const sortedBreeds = [...breeds].sort((a, b) =>
        a.name.localeCompare(b.name),
    );
    const popularBreedsByName = new Map(
        sortedBreeds.map((breed) => [breed.name.trim().toLowerCase(), breed]),
    );
    const seededPopularBreeds = popularBreedNames
        .map((name) => popularBreedsByName.get(name.toLowerCase()))
        .filter((breed): breed is (typeof sortedBreeds)[number] =>
            Boolean(breed),
        );
    const popularBreedIdSet = new Set(
        seededPopularBreeds.map((breed) => breed.id),
    );
    return [
        ...seededPopularBreeds,
        ...sortedBreeds.filter((breed) => !popularBreedIdSet.has(breed.id)),
    ]
        .slice(0, 12)
        .map((breed) => ({
            id: breed.id,
            name: breed.name,
            size: breed.size,
            imageUrl: breed.imageUrl,
        }));
}

function logHydratedCollections(
    feedListingIds: string[],
    compatibilityScoresByListingId: Record<string, { listingId: string }>,
    feedListings: CollectionWagsListing[] | undefined,
    findFeed: FindFeedBundledResponse["result"] | undefined,
) {
    if (!import.meta.env.DEV || typeof window === "undefined") return;
    const feedListingIdSet = new Set(feedListingIds);
    console.log("[find-feed] hydrated collections", {
        compatibilityScores: Object.values(
            compatibilityScoresByListingId,
        ).filter((score) => feedListingIdSet.has(score.listingId)),
        feedListingIds,
        feedListings,
        findFeed,
    });
}

export function useFindFeed({
    resolvedLocation,
    isResultsMode,
}: {
    resolvedLocation: { lat: number; lng: number } | null;
    isResultsMode: boolean;
}) {
    const { data: breeds = [] } = useLiveQuery(breedsLiveQueryOptions);
    const popularBreeds = getPopularBreeds(breeds);

    const findFeedQuery = getFindFeedQueryOptions(
        resolvedLocation ? findFeedQueryInput(resolvedLocation) : null,
    );
    const findFeedEnabled = Boolean(!isResultsMode && resolvedLocation);

    const {
        data: queriedFindFeed,
        isLoading: findFeedQueryIsLoading,
        isFetching: findFeedIsFetching,
        isError: findFeedQueryIsError,
        isPlaceholderData: findFeedIsPlaceholderData,
        isFetchedAfterMount: findFeedIsFetchedAfterMount,
        refetch: refetchFindFeed,
    } = useQuery({
        ...findFeedQuery,
        enabled: findFeedEnabled,
        placeholderData: (previousData) => previousData,
    });
    const { data: findFeedQueryData, isLoading: findFeedIsLoading } =
        useHydratedValue(
            { data: queriedFindFeed, isLoading: findFeedQueryIsLoading },
            { data: undefined, isLoading: findFeedEnabled },
        );
    const findFeed = findFeedQueryData?.result;
    const compatibilityScoresByListingId =
        findFeedQueryData?.sidecars.compatibilityScores?.byListingId ?? {};

    const listingIds = feedListingIds(findFeed);
    const { data: feedListings } = useWagsListingsLiveQuery(listingIds);
    const nonEmptyRows = nonEmptyFeedRows(
        projectFeedRows(findFeed, feedListings ?? []),
    );
    const hasFeedDogs = nonEmptyRows.length > 0;
    const findFeedIsError =
        findFeedQueryIsError &&
        (!findFeedQueryData || findFeedIsPlaceholderData);

    logHydratedCollections(
        listingIds,
        compatibilityScoresByListingId,
        feedListings,
        findFeed,
    );

    const feedLoadUi = resolveFindLoadUi({
        hasDogs: hasFeedDogs,
        isLoading: findFeedIsLoading,
        isFetching: findFeedIsFetching,
        isError: findFeedIsError,
        isFetchedAfterMount: findFeedIsFetchedAfterMount,
        isPlaceholderData: findFeedIsPlaceholderData,
    });

    return {
        popularBreeds,
        findFeedQuery,
        findFeedIsFetching,
        feedLoadUi,
        retryFindFeed: () => refetchFindFeed(),
        nonEmptyRows,
        hasFeedDogs,
        compatibilityScoresByListingId,
    };
}

export type FindFeedData = ReturnType<typeof useFindFeed>;
